/**
 * Cookie consent, BLOCKING modal (EU/UK).
 *
 * Consent Mode v2 defaults everything to DENIED in the inline <head> script
 * (index.html), which now loads Assembly's GTM container on every venue's
 * site (no more host gate). This modal covers the whole viewport and must be
 * answered before the visitor can interact with the site, they choose
 * Accept or Reject (both equally available; the site works with
 * essential-only on Reject). On a choice we:
 *   - persist it in localStorage (`assembly_consent`), which the <head>
 *     script re-applies on the next visit before any tag fires;
 *   - call gtag('consent','update', …) so Consent Mode flips live this session;
 *   - push `{ event: 'consent_update', state: true|false }` to the dataLayer so
 *     GTM can trigger on the choice (true = accepted, false = rejected).
 */
import { useEffect, useState } from "react";
import { useVenue } from "../lib/venue";
import { resolveFestivalContent } from "../lib/festival-content";
import { onAnalyticsConsentGranted } from "../lib/analytics";
import { isNativeApp } from "../lib/platform";

const STORAGE_KEY = "assembly_consent";

type ConsentWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: Array<Record<string, unknown>>;
};

export function ConsentBanner() {
  const [show, setShow] = useState(false);
  // Accent from the festival config (appConfig.festival.theme → seed), same
  // source as the "Buy tickets" CTA, so the Accept button matches the brand.
  const { data: venueData } = useVenue();
  const theme = resolveFestivalContent(venueData?.venue)?.theme;

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Native apps (Capacitor shells) must NOT show a web cookie banner, tracking
    // consent there is the OS's job (App Tracking Transparency / Play), and a web
    // consent modal inside the app looks wrong and risks App Store review issues.
    // Consent Mode stays at its default-denied, so no tracking cookies are set.
    if (isNativeApp()) return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setShow(true);
    } catch {
      // localStorage blocked (private mode), leave default-denied in place.
    }
  }, []);

  function decide(granted: boolean) {
    try {
      localStorage.setItem(STORAGE_KEY, granted ? "granted" : "denied");
    } catch {
      /* ignore persistence failure, the session-level update below still applies */
    }
    const w = window as unknown as ConsentWindow;
    const value = granted ? "granted" : "denied";
    // Update Consent Mode in BOTH directions: accept grants, reject re-denies.
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", {
        ad_storage: value,
        analytics_storage: value,
        ad_user_data: value,
        ad_personalization: value,
      });
    }
    // dataLayer event for GTM triggers, boolean `state` (true = accepted).
    w.dataLayer?.push({ event: "consent_update", state: granted });
    // Load any client GTM container that was held back pending this consent.
    if (granted) onAnalyticsConsentGranted();
    setShow(false);
  }

  if (!show) return null;

  // Bottom banner, styled DARK regardless of the OS light/dark setting: the
  // festival is always dark, and the token palette follows prefers-color-scheme,
  // so a light-mode phone would otherwise render a white card with unreadable
  // muted text over the dark site. Fixed dark chrome keeps it legible + on-theme;
  // the brand accent (gold) still comes from the festival config.
  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-white/10 bg-neutral-900/95 p-4 text-white shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-white/75">
          We use cookies to understand how the site is used. Accept analytics cookies,
          or continue with only what’s essential.{" "}
          <a href="/privacy" className="underline hover:text-white">
            Privacy policy
          </a>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => decide(false)}
            className="rounded-full border border-white/20 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10"
          >
            Reject
          </button>
          {/* Brand accent from festival config; falls back to a sensible gold. */}
          <button
            type="button"
            onClick={() => decide(true)}
            style={{
              backgroundColor: theme?.accent ?? "#E4A843",
              color: theme?.accentFg ?? "#1a1206",
            }}
            className="rounded-full px-4 py-2 text-sm font-semibold transition hover:brightness-105"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
